import { ref, computed } from 'vue'

import { getDispatchDriverRejectPendingPage, getDispatchWasteOrderPage, DISPATCH_TAB_TO_API } from '@/api/dispatchWasteOrder'

import { createDispatchAuditWs } from '@/utils/dispatchWs'

import { isLoggedIn } from '@/utils/auth'

import { playTodoRemind, unlockNotifyAudio } from '@/utils/notifyRemind'



const rejectPendingCount = ref(0)

const orderPendingCount = ref(0)

/** idle | connecting | online | reconnecting | offline */

const pushStatus = ref('idle')



let started = false

let refreshing = false

let wsClient = null

let visibilityHandler = null

let unlockBound = false

/** @type {Set<(content: object) => void>} */

const remindListeners = new Set()



const PUSH_STATUS_META = {

  idle: { label: '推送未启动', tone: 'muted' },

  connecting: { label: '推送连接中', tone: 'warn' },

  online: { label: '实时推送已连接', tone: 'ok' },

  reconnecting: { label: '推送重连中', tone: 'warn' },

  offline: { label: '推送已断开', tone: 'danger' }

}



function toCount(total) {

  const n = Number(total)

  if (!Number.isFinite(n) || n < 0) return 0

  return Math.floor(n)

}



async function refreshBadges() {

  if (!isLoggedIn() || refreshing) {

    return

  }

  refreshing = true

  try {

    const [rejectPage, orderPage] = await Promise.all([

      getDispatchDriverRejectPendingPage({ pageNo: 1, pageSize: 1 }),

      getDispatchWasteOrderPage({ tab: DISPATCH_TAB_TO_API.pending, pageNo: 1, pageSize: 1 })

    ])

    rejectPendingCount.value = toCount(rejectPage && rejectPage.total)

    orderPendingCount.value = toCount(orderPage && orderPage.total)

  } catch (err) {

    console.warn('[auditBadge] 刷新角标失败', err)

  } finally {

    refreshing = false

  }

}



function onWsRemind(content) {

  refreshBadges()

  remindListeners.forEach((fn) => {

    try {

      fn(content || {})

    } catch (err) {

      console.warn('[auditBadge] remind listener failed', err)

    }

  })

  // 仅「新增待办」播提示音；审核通过/驳回/接单完成只刷新角标

  const action = content && content.action

  if (action === 'refresh') {

    return

  }

  // 合同已改由管理后台审核，不再对运输端播合同提醒

  if (content && content.kind === 'contract') {

    return

  }

  playTodoRemind()

}



function onWsStatus(status) {

  pushStatus.value = status || 'offline'

}



function bindUnlockOnce() {

  if (unlockBound) return

  unlockBound = true

  const unlockOnce = () => {

    unlockNotifyAudio()

    document.removeEventListener('click', unlockOnce, true)

    document.removeEventListener('keydown', unlockOnce, true)

  }

  document.addEventListener('click', unlockOnce, true)

  document.addEventListener('keydown', unlockOnce, true)

}



function startAuditRemind() {

  if (!isLoggedIn()) {

    return

  }

  // 已启动则只确保连接存在；HMR / 异常后允许重建

  if (started && wsClient) {

    if (pushStatus.value !== 'online' && pushStatus.value !== 'connecting' && pushStatus.value !== 'reconnecting') {

      wsClient.connect()

    }

    return

  }

  started = true

  refreshBadges()

  wsClient = createDispatchAuditWs(onWsRemind, onWsStatus)

  wsClient.connect()

  bindUnlockOnce()



  if (!visibilityHandler) {

    visibilityHandler = () => {

      if (document.visibilityState === 'visible' && isLoggedIn()) {

        refreshBadges()

        if (wsClient && pushStatus.value !== 'online') {

          wsClient.connect()

        }

      }

    }

    document.addEventListener('visibilitychange', visibilityHandler)

  }

}



/** 顶部状态点击：手动重连 */

function reconnectAuditRemind() {

  if (!isLoggedIn()) {

    return

  }

  if (!wsClient) {

    started = false

    startAuditRemind()

    return

  }

  wsClient.connect()

}



function stopAuditRemind() {

  started = false

  if (visibilityHandler) {

    document.removeEventListener('visibilitychange', visibilityHandler)

    visibilityHandler = null

  }

  if (wsClient) {

    wsClient.close()

    wsClient = null

  }

  rejectPendingCount.value = 0

  orderPendingCount.value = 0

  pushStatus.value = 'offline'

}



/**

 * 订阅 WS 提醒（如订单页收到待接单推送后刷新列表）

 * @param {(content: object) => void} fn

 * @returns {() => void} unsubscribe

 */

function subscribeAuditRemind(fn) {

  if (typeof fn !== 'function') {

    return () => {}

  }

  remindListeners.add(fn)

  return () => {

    remindListeners.delete(fn)

  }

}



export function useAuditMenuBadge() {

  const pushStatusMeta = computed(() => PUSH_STATUS_META[pushStatus.value] || PUSH_STATUS_META.offline)

  return {

    rejectPendingCount,

    orderPendingCount,

    pushStatus,

    pushStatusMeta,

    refreshBadges,

    startAuditRemind,

    stopAuditRemind,

    reconnectAuditRemind,

    subscribeAuditRemind

  }

}
